import React from "react";
import { Sun, CheckCircle, Compass } from "@phosphor-icons/react";

const ROOFS = [
  { id: "south", label: "South-facing", best: true },
  { id: "east", label: "East-facing" },
  { id: "west", label: "West-facing" },
  { id: "mixed", label: "Multiple sides" },
];

export default function StepSolarStatus({ value, onChange, onContinue }) {
  const update = (k, v) => onChange({ ...value, [k]: v });

  return (
    <div data-testid="step-solar" className="max-w-3xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
          <Sun size={14} weight="fill" className="text-amber-600" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
            Existing solar?
          </span>
        </div>
        <h2 className="font-display text-4xl font-bold text-slate-950 tracking-tight">
          Do you already have solar?
        </h2>
        <p className="mt-3 text-slate-600">If yes, we'll account for it in your sizing.</p>
      </div>

      <div className="card-clean p-8 space-y-7">
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { id: false, label: "No solar yet", desc: "Most common" },
            { id: true, label: "Already have solar", desc: "Add or upgrade" },
          ].map((opt) => (
            <button
              key={String(opt.id)}
              type="button"
              onClick={() => update("has_existing_solar", opt.id)}
              data-testid={`solar-existing-${opt.id ? "yes" : "no"}`}
              className={`text-left p-5 rounded-2xl border-2 transition-all ${
                value.has_existing_solar === opt.id
                  ? "border-sky-500 bg-sky-50"
                  : "border-slate-200 hover:border-slate-300 bg-white"
              }`}
            >
              <div className="font-display font-semibold text-slate-900">{opt.label}</div>
              <div className="text-xs text-slate-500 mt-1">{opt.desc}</div>
            </button>
          ))}
        </div>

        {value.has_existing_solar && (
          <div className="grid sm:grid-cols-2 gap-5 bg-sky-50/60 rounded-2xl p-5 border border-sky-100">
            <label>
              <span className="block text-sm font-semibold text-slate-700 mb-2">
                Existing system size (kW)
              </span>
              <input
                type="number"
                step="0.1"
                value={value.existing_solar_kw}
                onChange={(e) => update("existing_solar_kw", parseFloat(e.target.value) || 0)}
                className="input-clean"
                data-testid="existing-kw-input"
              />
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => update("has_battery", !value.has_battery)}
                data-testid="has-battery-toggle"
                className={`flex-1 flex items-center gap-3 p-4 rounded-2xl border-2 transition-all ${
                  value.has_battery
                    ? "border-sky-500 bg-sky-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <CheckCircle
                  size={20}
                  weight="duotone"
                  className={value.has_battery ? "text-sky-600" : "text-slate-400"}
                />
                <span className="font-semibold text-slate-900">Has battery</span>
              </button>
            </div>
          </div>
        )}

        <div>
          <span className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Compass size={18} weight="duotone" className="text-amber-600" />
            Roof orientation
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {ROOFS.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => update("roof_orientation", r.id)}
                data-testid={`roof-${r.id}`}
                className={`text-center p-4 rounded-2xl border-2 transition-all ${
                  value.roof_orientation === r.id
                    ? "border-sky-500 bg-sky-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="font-semibold text-slate-900">{r.label}</div>
                {r.best && (
                  <div className="text-xs text-emerald-600 mt-1 font-semibold">Optimal</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-center">
        <button
          data-testid="solar-continue-btn"
          onClick={onContinue}
          className="btn-primary inline-flex items-center gap-2"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
